/**
 * Teams — "make a team, create a code, so others can join that team".
 *
 * PLAN.md §3 settles the shape and it is deliberately small: a team is a set of people and a set
 * of shared projects, with two roles, and no permission matrix. Everything here is pure, so the
 * join-code arithmetic and the membership rules are exercisable in Vitest without a DOM. The
 * reactive store and the persistence live in `teamspace.ts`, the same split `curriculum.ts` and
 * `workspace.ts` already have.
 *
 * ── The join code ──────────────────────────────────────────────────────────────────────────
 *
 * Eight characters, read aloud across a desk or typed off a phone screen. The alphabet is fixed
 * by a CHECK constraint in `supabase/0001_teams.sql` and this module matches it exactly — a code
 * this file can generate but the database will refuse is a bug that only appears in production.
 *
 * **Nothing ambiguous.** `0`/`O` and `1`/`I`/`L` are the pairs people actually get wrong, so none
 * of the five is in the alphabet — not "keep one and map the other", because a code you have to be
 * told the convention for is a code that gets mistyped. What remains is the 23 capitals and the
 * digits 2–9: **31 characters, not 32**, which is worth stating because it is easy to assume 32
 * and quote the wrong entropy.
 *
 * **Enough entropy to be un-guessable, not enough to be a secret.** log₂(31) × 8 = **39.63 bits**,
 * about 8.5 × 10¹¹ codes. See `CODE_BITS`. What that implies, plainly:
 *
 *   - Guessing one specific team's code takes ~4 × 10¹¹ tries on average. Fine.
 *   - Guessing ANY team's code is the number that matters, and it shrinks as the app grows: with
 *     a million live teams, one in 850,000 random codes hits something. **The server must rate
 *     limit join attempts** — a few per minute per caller — or 39.6 bits is not enough on its own.
 *     `join_team()` returns only an id and never the row, so it cannot be used as a richer oracle;
 *     rate limiting is what closes the rest.
 *   - Collisions between two freshly generated codes reach 50% likelihood at around 1.2 million
 *     live codes (birthday bound). The `unique` constraint on `teams.join_code` is what makes that
 *     a retry rather than a silent merge of two teams.
 *   - A join code is a bearer credential. Anyone holding it is in. It must therefore be
 *     rotatable, and this module treats it as data rather than identity — a team keeps its `id`
 *     when its `code` changes.
 */

import type { InkName } from './scene';
import { uuidv7 } from './merge';

/**
 * 31 characters: the capitals minus I, L and O, then the digits 2–9.
 *
 * Byte-identical to the CHECK constraint on `teams.join_code`. If one of the two ever changes,
 * the other has to change with it, and this comment is where to look.
 */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export const CODE_LENGTH = 8;

/** 39.63. Stated as a computation so it cannot drift from the alphabet it describes. */
export const CODE_BITS = Math.log2(CODE_ALPHABET.length) * CODE_LENGTH;

const bytes = (length: number): Uint8Array => {
  const out = new Uint8Array(length);
  const source = globalThis.crypto;
  if (source && typeof source.getRandomValues === 'function') return source.getRandomValues(out);
  for (let i = 0; i < length; i++) out[i] = Math.floor(Math.random() * 256);
  return out;
};

/**
 * A fresh code, uniformly distributed.
 *
 * Rejection sampling rather than a modulo. 256 is not a multiple of 31, so `byte % 31` would make
 * the first eight characters of the alphabet about 12% likelier than the rest — a bias that is
 * free to avoid, and biased randomness in a credential is the kind of detail that is embarrassing
 * to explain later.
 */
export function newJoinCode(): string {
  const limit = 256 - (256 % CODE_ALPHABET.length);
  let code = '';
  while (code.length < CODE_LENGTH) {
    for (const byte of bytes(CODE_LENGTH * 2)) {
      if (byte >= limit) continue;
      code += CODE_ALPHABET[byte % CODE_ALPHABET.length];
      if (code.length === CODE_LENGTH) break;
    }
  }
  return code;
}

/**
 * Uppercase, and drop the separators and whitespace people type. Nothing else is touched.
 *
 * This must run on the CLIENT before the code reaches the server. `join_team()` does no more than
 * `upper(trim(...))`, so the hyphen this app itself puts in when it displays a code would fail to
 * match the stored one — a bug where the app cannot read back what it printed.
 */
export const normaliseCode = (input: string): string =>
  input.toUpperCase().replace(/[\s\-–—_.]/g, '');

export const isJoinCode = (input: string): boolean => {
  const code = normaliseCode(input);
  return code.length === CODE_LENGTH && [...code].every(char => CODE_ALPHABET.includes(char));
};

/**
 * The characters in what was typed that could not be part of a code.
 *
 * Returned rather than silently stripped: a field that quietly deletes the `0` someone typed and
 * then says "that code is wrong" is a field that has hidden the reason. The dialog names them.
 */
export const strayCharacters = (input: string): string[] =>
  [...new Set([...normaliseCode(input)].filter(char => !CODE_ALPHABET.includes(char)))];

/** `A7K2-M9PQ`. Grouped in fours because that is how a person reads eight characters aloud. */
export const formatCode = (code: string): string => {
  const clean = normaliseCode(code);
  return clean.length === CODE_LENGTH ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
};

// ── The team ───────────────────────────────────────────────────────────────────────────────

export type TeamRole = 'owner' | 'member';

export interface TeamMember {
  /** The person's stable local id — the same one presence uses as `memberId`. */
  id: string;
  name: string;
  /** A NAME, never a resolved colour. A member's cursor survives a theme change. */
  ink: InkName;
  role: TeamRole;
  joinedAt: number;
}

export interface Team {
  id: string;
  name: string;
  code: string;
  createdAt: number;
  updatedAt: number;
  members: TeamMember[];
  /** Shared work. PLAN.md Part II §9: an organisation shares PROJECTS, not courses. */
  projectIds: string[];
}

/**
 * The inks a member can be given.
 *
 * Six, drawn from the named set in scene.ts, so a peer's cursor and their in-progress stroke are
 * the same colour as the ink they draw with and both survive the paper changing. `highlight` is
 * excluded — a translucent yellow cursor is invisible on light paper.
 */
export const MEMBER_INKS: readonly InkName[] = [
  'violet', 'teal', 'amber', 'crimson', 'graphite', 'construction',
];

/**
 * The least-used ink, ties broken by the order above.
 *
 * Deterministic rather than random: two people joining at once from the same code should not both
 * get violet, and "whichever colour is free" is a rule anyone can predict.
 */
export function nextMemberInk(team: Pick<Team, 'members'>): InkName {
  const used = new Map<InkName, number>(MEMBER_INKS.map(ink => [ink, 0]));
  for (const member of team.members) used.set(member.ink, (used.get(member.ink) ?? 0) + 1);
  let best = MEMBER_INKS[0]!;
  for (const ink of MEMBER_INKS) if ((used.get(ink) ?? 0) < (used.get(best) ?? 0)) best = ink;
  return best;
}

export const newMember = (
  fields: { id?: string; name: string; ink?: InkName; role?: TeamRole },
  team: Pick<Team, 'members'> = { members: [] },
  at = Date.now(),
): TeamMember => ({
  id: fields.id ?? uuidv7(at),
  name: fields.name.trim() || 'Anonymous',
  ink: fields.ink ?? nextMemberInk(team),
  role: fields.role ?? 'member',
  joinedAt: at,
});

export function createTeam(
  name: string,
  owner: { id: string; name: string; ink?: InkName },
  at = Date.now(),
): Team {
  return {
    id: uuidv7(at),
    name: name.trim() || 'Untitled team',
    code: newJoinCode(),
    createdAt: at,
    updatedAt: at,
    members: [newMember({ ...owner, role: 'owner', ink: owner.ink ?? MEMBER_INKS[0] }, { members: [] }, at)],
    projectIds: [],
  };
}

const touch = (team: Team, changes: Partial<Team>, at = Date.now()): Team =>
  ({ ...team, ...changes, updatedAt: at });

export const memberById = (team: Team, id: string): TeamMember | undefined =>
  team.members.find(member => member.id === id);

export const isOwner = (team: Team, memberId: string): boolean =>
  memberById(team, memberId)?.role === 'owner';

/** Joining twice is not an error — it updates the name and keeps the ink already assigned. */
export function addMember(team: Team, member: TeamMember, at = Date.now()): Team {
  const existing = memberById(team, member.id);
  if (existing) {
    return touch(team, {
      members: team.members.map(m => (m.id === member.id ? { ...m, name: member.name } : m)),
    }, at);
  }
  return touch(team, { members: [...team.members, member] }, at);
}

/**
 * Leaving, including by the owner.
 *
 * The oldest remaining member inherits `owner`, because a team whose only owner has left is a
 * team nobody can rotate the code on — and an unrotatable bearer credential is the failure this
 * one line prevents. A team with no members left is returned empty for the caller to delete.
 */
export function removeMember(team: Team, memberId: string, at = Date.now()): Team {
  const leaving = memberById(team, memberId);
  if (!leaving) return team;
  const members = team.members.filter(member => member.id !== memberId);
  if (leaving.role === 'owner' && members.length > 0 && !members.some(m => m.role === 'owner')) {
    const heir = [...members].sort((a, b) => a.joinedAt - b.joinedAt)[0]!;
    return touch(team, { members: members.map(m => (m.id === heir.id ? { ...m, role: 'owner' } : m)) }, at);
  }
  return touch(team, { members }, at);
}

export const renameTeam = (team: Team, name: string, at = Date.now()): Team =>
  (name.trim() ? touch(team, { name: name.trim() }, at) : team);

/** A new code. The old one stops working the moment this is stored — that is the point of it. */
export const rotateCode = (team: Team, at = Date.now()): Team =>
  touch(team, { code: newJoinCode() }, at);

export const shareProject = (team: Team, projectId: string, at = Date.now()): Team =>
  (team.projectIds.includes(projectId)
    ? team
    : touch(team, { projectIds: [...team.projectIds, projectId] }, at));

export const unshareProject = (team: Team, projectId: string, at = Date.now()): Team =>
  touch(team, { projectIds: team.projectIds.filter(id => id !== projectId) }, at);

export const findByCode = (teams: readonly Team[], code: string): Team | undefined => {
  const wanted = normaliseCode(code);
  return teams.find(team => team.code === wanted);
};

/**
 * The stored form, and the round trip that guards it.
 *
 * PLAN.md §6.8: every persisted field is read back, with a test asserting deep equality. No
 * mapping layer, for the same reason `StoredScene` has none — a field cannot be forgotten in a
 * mapping that does not exist.
 */
export interface StoredTeam extends Team {
  version: 1;
}

export const toStoredTeam = (team: Team): StoredTeam => ({ ...structuredCloneish(team), version: 1 });

export function fromStoredTeam(stored: unknown): Team | null {
  if (!stored || typeof stored !== 'object') return null;
  const team = stored as StoredTeam;
  if (typeof team.id !== 'string' || !Array.isArray(team.members)) return null;
  const { version: _version, ...rest } = team;
  return {
    ...rest,
    projectIds: Array.isArray(team.projectIds) ? [...team.projectIds] : [],
    members: team.members.map(member => ({ ...member })),
  };
}

const structuredCloneish = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
